import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SetActivePermission, UserLogout } from '../actions/authActions';
import { fetchActiveProcesses, SelectProcess } from '../actions/processActions';
import { loadMatrixPei } from '../actions/peiActions'
import { buildPermissionKey, findPermissionByKey, mapPermissionsToSelectOptions } from '../utils/permissionHelpers.js';
import { useActivePermission } from '../hooks/useActivePermission';
import { useUserAvatar } from '../hooks/useUserAvatar';

import { Container, Divider, Text, SimpleGrid, Group, Box, Card, Avatar, Select, ScrollArea, Skeleton, Button, Stack, Center, rem, Title } from '@mantine/core';
import { IconCircleDashedX, IconHourglass, IconLogout, IconSettings } from '@tabler/icons-react';

import ProcessCard from '../components/Process/ProcessCard';
import EmptyProcessCard from '../components/Process/EmptyProcessCard';
import { useMediaQuery } from '@mantine/hooks';

const ProcessSelectorPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activesProcess, historicalProcess, loading } = useSelector(state => state.process);
  const { user, permissions } = useSelector((state) => state.auth);

  const activePermission = useActivePermission();
  const permissionOptions = mapPermissionsToSelectOptions(permissions.data);
  const currentPermission = activePermission ? buildPermissionKey(activePermission) : '';
  const isAdmin = user.roles.includes('administrador');
  const isLoading = (activesProcess.length === 0 && historicalProcess === 0 ) && loading;

  const isMobile = useMediaQuery('(max-width: 50em)');
  const avatarText = useUserAvatar(user);
  
  const userNames = user?.username?.includes(',') 
    ? user.username.split(',')[1]?.trim() 
    : user?.username || '';

  useEffect(() => {
    dispatch(fetchActiveProcesses(null,null,user));
  }, [dispatch, activePermission]);
  
  const handleSelectProceso = (process) => {
    dispatch(SelectProcess(process));
    dispatch(loadMatrixPei(process.matriz.Id, user));
    navigate('/backoffice/home');
  };

  const handlePermissionChange = (value) => {
    const selected = findPermissionByKey(permissions.data, value);
    if (selected) {
      dispatch(SetActivePermission(selected));
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    dispatch(UserLogout());
    navigate('/');
  };

  return (
    <Container size="80%" py="xl">
      <section>
        <Stack gap={'md'}>
          <Group justify='space-between' align='center'>
            <Box>
              <Title order={1} fw={400} ta={isMobile ? 'center' : 'start'}>Bienvenido al sistema de Monitoreo <Text span inherit c={'#e72166'}>PEI</Text></Title>
            </Box>
            <Group align="flex-start" justify='flex-end'>
              {isAdmin && (
                <Button 
                  variant='default'  
                  onClick={() => navigate('/admin')} 
                  leftSection={<IconSettings size={18}/>}
                >
                  Administracion
                </Button>
              )}
              <Button 
                color='red'
                onClick={handleLogout} 
                leftSection={<IconLogout size={18} />}
              >
                Cerrar Sesión
              </Button>
            </Group>
          </Group>
          <Card withBorder h={'100%'} radius={'md'}> 
            <Group wrap='nowrap' justify='space-between'>
              <Center inline>
                <Avatar radius="xl" size='lg' color="initials" name={avatarText}/>
                <Box ml={'md'}>
                  <Text size='lg' fw={500}>{user?.username}</Text>
                  <Text c={'dimmed'}>{user?.usermail}</Text>
                </Box>
              </Center>
              <Stack gap={'xs'}>
                <Text size='sm'>Estas ingresando como:</Text>
                <Select
                  data={permissionOptions}
                  value={currentPermission}
                  onChange={handlePermissionChange}
                  placeholder="Cambiar rol"
                  allowDeselect={false}
                  checkIconPosition="right"
                />
              </Stack>
            </Group>
          </Card>
        </Stack>
      </section>

      <Box mt={rem(50)}>
        <Stack mb="lg" gap={'xs'}>
          <Text size='1.5rem'  fw={500}>Procesos Activos</Text>
          <Text >
            Elija el proceso de evaluación en el que desea trabajar. Puede cambiar entre procesos en cualquier momento.
          </Text>
        </Stack>
        <SimpleGrid cols={{ base: 1, sm: 2, md:3}}>
          {isLoading ? (
            <Skeleton height={300} width={'100%'}/>
          ) : activesProcess?.map((p) => (
            <ProcessCard key={p.Id} process={p} onSelect={handleSelectProceso} />
          ))
          }
          { (activesProcess.length < 2) && !isLoading && (
            <EmptyProcessCard
              icon={<IconHourglass/>}
              title={"Próximamente"}
              description={"Cuando haya nuevos procesos disponibles, se habilitarán aquí para que puedas participar."}
            />
          )}
        </SimpleGrid>
      </Box>

      <Divider my="xl" />

      <Box>
        <Text size='1.5rem' mb="md" fw={500}>Históricos</Text>
        <ScrollArea type="always" offsetScrollbars>
          {isLoading ? (
            <Skeleton height={240} width={260}/>
          ):(
            <Group wrap='nowrap' p={'md'}>
              { historicalProcess?.length > 0 ? (
                historicalProcess?.map((p) => (
                  <Box w={280} key={p.Id}>
                    <ProcessCard process={p} onSelect={handleSelectProceso} isHistorical/>
                  </Box>
                ))
              ) : (
                <Box w={280}>
                  <EmptyProcessCard
                    icon={<IconCircleDashedX/>}
                    title={null}
                    description={'No hay procesos históricos disponibles'}
                  />
                </Box>
              )}
            </Group>
          )}
        </ScrollArea>
      </Box>
    </Container>
  );
};

export default ProcessSelectorPage;
